import { orderBy } from 'lodash';
import { DataObjectList, ObjectClassGenerator } from '../db/dataobject';
import { getMemoryTable } from '../db/datamanager';
import type { Feature, MultiPolygon, Polygon, Position } from 'geojson';
import Mission, { MissionList } from './MissionClass';
import Boundary, { BoundaryList } from './BoundaryClass';
import SampleZone, { SampleZoneList } from './SampleZoneClass';
import {
  to_kml,
  load_kml,
  get_color,
  contains,
  normalize,
  to_feature,
  new_zone,
  get_color_array,
  finish_zone,
  getContainingOuterBoundary,
  toMultiPolygon,
} from '../db_ops/zone_ops';
import { IKMLElement } from '../kml';
import { ZoneRecordingType, ZoneType } from './ZoneTypesDatatype';
import { Coordinate } from 'ol/coordinate';
import { IZone } from './types';
import { MINIMUM_COORDINATES_FOR_VALID_BOUNDARY } from '../constants';
import { dispatchMissionBoundaryUpdated, dispatchMissionUpdated } from '../missionEvents';
import { dispatchSamplesUpdated, dispatchZonesUpdated } from '../mapEvents';
import { Poly } from '../types/types';
import { polyContainsPoly, polyOverlapsPoly } from '../services/zones/poly_utils';
import { alertError } from '../alertDispatcher';
import { GeoDataUtilities, normalizePolygonFeature } from '@rogoag/shared-ts';

const VISUAL_ZONE_SUFFIX = '_v_i_s';

export default class Zone extends ObjectClassGenerator<Zone>('Zone') implements IZone {
  // attributes
  #zone_name: string;
  #zone_type: ZoneType;
  #visible: boolean;
  #labels_visible: boolean;

  // relationships
  #Mission_id: number | undefined;

  static tableName = 'Zone';

  constructor(state = {}) {
    super(state);
    // publish persistent attributes
    this.publishAttribute(Zone, 'zone_name');
    this.publishAttribute(Zone, 'zone_type');
    this.publishAttribute(Zone, 'visible');
    this.publishAttribute(Zone, 'labels_visible');
    this.publishAttribute(Zone, 'Mission_id');
    // initialize state
    this.initializeState(state);
  }

  initializeState(state: Partial<Zone> = {}) {
    this._instance_id = state._instance_id!;
    this._refs = { ...state._refs };
    this._version = state._version!;
    this.#zone_name = state.zone_name || '';
    this.#zone_type = state.zone_type || 0;
    this.#visible = typeof state.visible === 'boolean' ? state.visible : true;
    this.#labels_visible = typeof state.labels_visible === 'boolean' ? state.labels_visible : true;
    this.#Mission_id = state.Mission_id;
  }

  dispose() {
    const mission = this.getMission();

    if (mission) {
      this.Mission_id = undefined;
    }

    for (const boundary of this.getOuterZoneBoundarys()) {
      boundary.OuterZone_id = undefined;

      if (!boundary.getInnerZone()) {
        boundary.dispose();
      }
    }

    for (const boundary of this.getInnerZoneBoundarys()) {
      boundary.InnerZone_id = undefined;

      if (!boundary.getOuterZone()) {
        boundary.dispose();
      }
    }

    const samplezone = this.getSampleZone();

    if (samplezone) {
      samplezone.Zone_id = undefined;
      samplezone.dispose();
    }

    Zone.delete(this.instance_id);
  }

  set zone_name(value) {
    this.#zone_name = value;
    this.last_modified = Date.now() / 1000;
    this.syncToDB();
  }

  get zone_name() {
    return this.#zone_name;
  }

  set zone_type(value) {
    this.#zone_type = value;
    this.last_modified = Date.now() / 1000;
    this.syncToDB();
  }

  get zone_type() {
    return this.#zone_type;
  }

  set visible(value) {
    this.#visible = value;
    this.last_modified = Date.now() / 1000;
    this.syncToDB();
  }

  get visible() {
    return this.#visible;
  }

  set labels_visible(value) {
    this.#labels_visible = value;
    this.last_modified = Date.now() / 1000;
    this.syncToDB();
  }

  get labels_visible() {
    return this.#labels_visible;
  }

  get mission_name() {
    return this.getMission()?.name;
  }

  get last_modified() {
    return this.getMission()?.last_modified;
  }

  set last_modified(value) {
    const inst = this.getMission();

    if (inst && value) {
      inst.last_modified = value;
    }
  }

  set Mission_id(value) {
    if (this.#Mission_id) {
      const relateObj = Mission.get(this.#Mission_id);

      if (relateObj) {
        relateObj.removeRelationshipData('Zone', this.instance_id);
      }
    }

    this.#Mission_id = value;

    if (value) {
      const relateObj = Mission.get(value);

      if (relateObj) {
        relateObj.addRelationshipData('Zone', this.instance_id);
      }
    }

    this.last_modified = Date.now() / 1000;
    this.syncToDB();
  }

  get Mission_id() {
    return this.#Mission_id;
  }

  getMission() {
    return Mission.get(this.Mission_id);
  }

  getOuterZoneBoundarys() {
    if (this._refs && this._refs.OuterZone) {
      return new BoundaryList(
        ...Array.from(this._refs.OuterZone)
          .map((id) => Boundary.get(id))
          .filter((sel) => !!sel),
      );
    } else {
      const boundarys = Boundary.query((sel) => sel && sel.OuterZone_id === this.instance_id);

      for (const boundary of boundarys) {
        boundary.OuterZone_id = this.instance_id; // re-set foreign key to force update of _refs
      }

      return new BoundaryList(...boundarys);
    }
  }

  polyContainsCoordinate(poly: Poly, lat: number, lon: number): boolean {
    const ob = poly.outerBoundary;
    const ibs = poly.innerBoundaries;

    if (!ob) {
      return false;
    }

    // An outer boundary has to contain the point
    let contained = ob.contains(lat, lon);

    for (const ib of ibs) {
      // All inner boundaries have to exclude the point
      contained = contained && !ib.contains(lat, lon);
    }

    return contained;
  }

  getOuterBoundaryByPolyId(polyId: number): Boundary | undefined {
    const outerBoundaries = this.getOuterZoneBoundarys();

    return outerBoundaries.find((boundary) => boundary.poly_id === polyId);
  }

  disposePolyById(polyId: number) {
    const outerBoundary = this.getOuterBoundaryByPolyId(polyId);

    if (outerBoundary) {
      outerBoundary.dispose();
    }

    const innerBoundaries = this.getInnerZoneBoundarys().filter((boundary) => boundary.poly_id === polyId);

    for (const innerBoundary of innerBoundaries) {
      innerBoundary.dispose();
    }
  }

  getPolysGroupedByPolyId(): { [polyId: number]: Poly } {
    const outerBoundaries = orderBy(this.getOuterZoneBoundarys(), ['poly_id'], ['asc']);
    const innerBoundaries = orderBy(this.getInnerZoneBoundarys(), ['boundary_id'], ['asc']);

    const groupedByPolyId: { [polyId: number]: Poly } = {};

    for (const ob of outerBoundaries) {
      if (!groupedByPolyId[ob.poly_id]) {
        groupedByPolyId[ob.poly_id] = {
          polyId: ob.poly_id,
          innerBoundaries: [] as Boundary[],
        } as Poly;
      }

      groupedByPolyId[ob.poly_id].outerBoundary = ob;
    }

    for (const ib of innerBoundaries) {
      // This should not happen if all boundaries are correct: we should not
      // have a hole that doesn't have any outer boundary associated with it.
      // So, technically, that's an error. However, throwing an error here
      // is not safe until we're sure that we're parsing all edge cases correctly.
      if (!groupedByPolyId[ib.poly_id]) {
        groupedByPolyId[ib.poly_id] = {
          polyId: ib.poly_id,
          innerBoundaries: [] as Boundary[],
        } as Poly;
      }

      groupedByPolyId[ib.poly_id].innerBoundaries.push(ib);
    }

    return groupedByPolyId;
  }

  getOverlappingPolyIds(): string[] {
    const polys = this.getPolysGroupedByPolyId();
    const polyIds = Object.keys(polys);

    if (polyIds.length === 0) {
      return [];
    }

    const out = new Set<string>();

    for (const polyAId of polyIds) {
      for (const polyBId of polyIds) {
        // Do not check a poly against itself
        if (polyAId === polyBId) {
          continue;
        }

        if (polyOverlapsPoly(polys[polyAId], polys[polyBId])) {
          out.add(polyAId);
          out.add(polyBId);
        }
      }
    }

    return Array.from(out);
  }

  async renameWithUnderlyingSample(newName: string) {
    const mission = this.getMission();

    if (!mission) {
      return;
    }

    this.zone_name = newName;
    const sampleZone = this.getSampleZone();

    if (!sampleZone) {
      // Only the zone_name changed. No sample / core mutation — so
      // fire the narrow zones-only event instead of the catch-all
      // mission event (which would rebuild ~1000 cores on Rose 6).
      // Step 12 of docs/superpowers/plans/2026-04-08-kml-layer-split.md.
      dispatchZonesUpdated();

      return;
    }

    const sample = sampleZone.getSample();

    if (!sample) {
      // Same as above — zone_name only.
      dispatchZonesUpdated();

      return;
    }

    // Capture the OLD sample_id before the rename so we can tell
    // CorePointsLayer / CentroidsLayer to drop the stale entry and
    // build the new one.
    const oldSampleId = sample.sample_id;

    try {
      await mission.rename_sample(sample.sample_id, newName);
    } catch (error) {
      alertError(`Error renaming sample: ${error}`);
    }

    // Happy path: a sample was also renamed. Targeted rebuild via
    // both old and new ids — rebuildForSamples handles the rename
    // by removing the old-key entry and building the new-key one.
    dispatchMissionUpdated();
    dispatchSamplesUpdated([oldSampleId, newName]);
  }

  removeCoresGeoLocatedInSampleZone() {
    if (this.zone_type !== ZoneType.SAMPLE_ZONE) {
      return;
    }

    const mission = this.getMission();

    if (!mission) {
      return;
    }

    const outerBoundaries = this.getOuterZoneBoundarys();

    if (outerBoundaries.length === 0) {
      return;
    }

    const polys = this.getPolysGroupedByPolyId();
    const polyIds = Object.keys(polys);

    if (polyIds.length === 0) {
      return;
    }

    const allCores = mission.getAllCores();

    for (const core of allCores) {
      polyIds.forEach((polyId) => {
        const contained = this.polyContainsCoordinate(polys[polyId], core.lat, core.lon);

        if (contained) {
          core.dispose();
        }
      });
    }
  }

  getInnerZoneBoundarys() {
    if (this._refs && this._refs.InnerZone) {
      return new BoundaryList(
        ...Array.from(this._refs.InnerZone)
          .map((id) => Boundary.get(id))
          .filter((sel) => !!sel),
      );
    } else {
      const boundarys = Boundary.query((sel) => sel && sel.InnerZone_id === this.instance_id);

      for (const boundary of boundarys) {
        boundary.InnerZone_id = this.instance_id; // re-set foreign key to force update of _refs
      }

      return new BoundaryList(...boundarys);
    }
  }

  getSampleZone() {
    if (this._refs && this._refs.SampleZone) {
      const result = Array.from(this._refs.SampleZone).map((id) => SampleZone.get(id));

      return result[0];
    } else {
      const samplezone = SampleZone.findOne((sel) => sel?.Zone_id === this.instance_id);

      if (samplezone) {
        samplezone.Zone_id = this.instance_id; // re-set foreign key to force update of _refs
      }

      return samplezone;
    }
  }

  async checkIntegrity() {
    const problems: string[] = [];
    // check uniqueness
    // check ID1
    const id1_duplicates = Zone.query(
      (sel) =>
        sel.instance_id !== this.instance_id &&
        sel.mission_name === this.mission_name &&
        sel.zone_name === this.zone_name &&
        sel.last_modified === this.last_modified,
    );

    for (const dup of id1_duplicates) {
      problems.push(`Duplicate zone found with ID1 for instance id ${this.instance_id}: ${dup.instance_id} (${dup})`);
    }

    // check relationships
    for (let tableName in this._refs) {
      const refName = tableName;
      tableName = 'Boundary';
      Array.from(this._refs[refName]).forEach((key) => {
        if (!getMemoryTable(tableName)?.getOne(key)) {
          problems.push(`zone: Could not find ${tableName} instance for ID: ${key}`);
        }
      });
    }

    if (!this.getMission()) {
      problems.push(`zone: Could not find mission instance across unconditional relationship R14: ${this.instance_id}`);
    }

    if (this.getOuterZoneBoundarys().length < 1) {
      problems.push(
        `zone: Could not find boundary instance across unconditional relationship R15: ${this.instance_id}`,
      );
    }

    return problems;
  }

  // @ts-ignore TODO fix kml serialize typings
  to_kml() {
    return to_kml.bind(this)();
  }

  static async load_kml(poly: IKMLElement, mission: Mission) {
    return await load_kml(poly, mission);
  }

  get_color() {
    return get_color.bind(this)();
  }

  contains(lat: number, lon: number) {
    return contains.bind(this)(lat, lon);
  }

  hasValidBoundaries(deepCheck: boolean = false, maxAllowedIntersectionArea: number = 200): boolean {
    return this.validateBoundaries(deepCheck, maxAllowedIntersectionArea).isValid;
  }

  validateBoundaries(
    deepCheck: boolean = false,
    maxAllowedIntersectionArea: number = 200,
  ): {
    isValid: boolean;
    defectiveGeometries?: Feature<GeoJSON.Point | GeoJSON.Polygon>[];
    warningGeometries?: Feature<GeoJSON.Point | GeoJSON.Polygon>[];
    message?: string;
  } {
    const outer = this.getOuterZoneBoundarys();

    if (outer.length < 1) {
      return { isValid: false, message: 'No outer boundaries', defectiveGeometries: [] };
    }

    // Check outer boundaries
    for (const boundary of outer) {
      if (boundary.coordinates.length < MINIMUM_COORDINATES_FOR_VALID_BOUNDARY) {
        return { isValid: false, message: 'Insufficient coordinates for valid boundary', defectiveGeometries: [] };
      }
    }

    // Check inner boundaries
    for (const boundary of this.getInnerZoneBoundarys()) {
      if (boundary.coordinates.length < MINIMUM_COORDINATES_FOR_VALID_BOUNDARY) {
        return {
          isValid: false,
          message: 'Insufficient coordinates for valid inner boundary',
          defectiveGeometries: [],
        };
      }
    }

    if (deepCheck) {
      const result = GeoDataUtilities.isPolygonValid(this.toMultiPolygon(), maxAllowedIntersectionArea);

      if (!result.isValid) {
        return {
          isValid: false,
          defectiveGeometries: result.defectiveGeometries || [],
          warningGeometries: result.warningGeometries,
          message: result.message,
        };
      }

      return { isValid: true, defectiveGeometries: [], warningGeometries: result.warningGeometries };
    }

    return { isValid: true, defectiveGeometries: [] };
  }

  getContainingOuterBoundary(lat: number, lon: number): Boundary | undefined {
    return getContainingOuterBoundary.bind(this)(lat, lon);
  }

  async normalize() {
    await normalize.bind(this)();
  }

  // TODO normalize this return type and make it better
  to_feature() {
    return to_feature.bind(this)();
  }

  toMultiPolygon(): Feature<MultiPolygon> {
    return toMultiPolygon.bind(this)();
  }

  static makeContainedPolygonsHoles(mission: Mission) {
    const allZones = mission.getZones();

    if (!allZones.length) {
      return;
    }

    allZones.forEach((zone: Zone) => {
      const groupedPolys = zone.getPolysGroupedByPolyId();

      for (const polyId in groupedPolys) {
        const polyA = groupedPolys[polyId];
        const ob = polyA.outerBoundary;

        if (!ob) {
          continue;
        }

        for (const polyBId in groupedPolys) {
          // Do not check a poly against itself.
          if (polyId === polyBId) {
            continue;
          }

          const polyB = groupedPolys[polyBId];
          const obB = polyB.outerBoundary;

          if (!obB) {
            continue;
          }

          // If a polyB contains holes, abort.
          if (polyB.innerBoundaries.length > 0) {
            continue;
          }

          // Check if polyB is contained within polyA.
          if (polyContainsPoly(polyA, polyB)) {
            mission.addPolyToZone(polyB.polyId, zone);
          }
        }
      }
    });

    dispatchMissionBoundaryUpdated();
    dispatchMissionUpdated();
  }

  static fixPolygons(mission: Mission) {
    const allZones = mission.getZones();

    if (!allZones.length) {
      return;
    }

    allZones.forEach((zone: Zone) => {
      const multiPolygonFeature = zone.toMultiPolygon();

      // normalizePolygonFeature reclassifies rings: holes that are actually
      // separate polygons get promoted, and vice versa
      const normalized = normalizePolygonFeature(multiPolygonFeature);

      if (normalized === multiPolygonFeature) {
        return; // no changes needed
      }

      // Extract polygon coordinate arrays from normalized geometry
      let polygons: Position[][][] = [];

      if (normalized.geometry.type === 'Polygon') {
        polygons = [(normalized.geometry as Polygon).coordinates];
      } else if (normalized.geometry.type === 'MultiPolygon') {
        polygons = (normalized.geometry as MultiPolygon).coordinates;
      } else {
        return; // non-polygon geometry, skip
      }

      // Capture old boundaries before creating new ones
      const oldOuter = [...zone.getOuterZoneBoundarys()];
      const oldInner = [...zone.getInnerZoneBoundarys()];

      // Rebuild boundaries from normalized polygons BEFORE disposing old ones.
      // Boundary.dispose() deletes the zone when it's the last outer boundary,
      // so new boundaries must exist first.
      // normalizePolygonFeature returns [lon, lat], boundaries store [lat, lon]
      for (const polygonCoords of polygons) {
        const outerRing = polygonCoords[0];
        const polyId = mission.assign_id() * -1;

        const ob = Boundary.create();
        ob.boundary_id = mission.assign_id();
        ob.poly_id = polyId;
        ob.OuterZone_id = zone.instance_id;
        ob.coordinates = outerRing.map((coord) => [coord[1], coord[0]]);

        for (let i = 1; i < polygonCoords.length; i++) {
          const ib = Boundary.create();
          ib.boundary_id = mission.assign_id();
          ib.poly_id = polyId;
          ib.InnerZone_id = zone.instance_id;
          ib.coordinates = polygonCoords[i].map((coord) => [coord[1], coord[0]]);
        }
      }

      // Dispose old boundaries after new ones are in place
      for (const bnd of [...oldOuter, ...oldInner]) {
        bnd.dispose();
      }
    });

    dispatchMissionBoundaryUpdated();
    dispatchMissionUpdated();
  }

  static async new_zone(
    name: ZoneRecordingType | string,
    points: Coordinate[][],
    mission_id: number,
  ): Promise<[Zone, any[]]> {
    return await new_zone(name, points, mission_id);
  }

  // This handles both - adding a new poly and adding a hole
  // inside the existing poly (because of calling addPolyToZone)
  // Returns new polyId if a poly is added or undefined if hole
  // or if some issue happened. TODO: handle the return more gracefully.
  async addNewPolyOrHole(rings: Coordinate[][]): Promise<Poly | undefined> {
    const mission = this.getMission();

    if (!mission) {
      return;
    }

    const polyId = mission.assign_id() * -1;

    for (const [i, ring] of rings.entries()) {
      await Boundary.new_boundary(ring, this, i === 0, polyId);
    }

    mission.addPolyToZone(polyId, this);

    return this.getPolysGroupedByPolyId()[polyId];
  }

  get_color_array() {
    return get_color_array.bind(this)();
  }

  async finish_zone() {
    await finish_zone.bind(this)();
  }

  static makeVisualZoneName(sampleZoneName: string): string {
    return `${sampleZoneName}${VISUAL_ZONE_SUFFIX}`;
  }

  static matchVisualZoneName(visualZoneName: string): boolean {
    const pattern = new RegExp(`^[a-zA-Z0-9]+${VISUAL_ZONE_SUFFIX}$`);

    return pattern.test(visualZoneName);
  }
}

export class ZoneList extends DataObjectList<Zone> {
  getMissions() {
    return new MissionList(...this.map((zone) => zone.getMission()).filter((sel) => !!sel));
  }

  getOuterBoundarys() {
    return new BoundaryList(
      ...this.reduce((zones: Boundary[], zone) => zones.concat(zone.getOuterZoneBoundarys()), []).filter(
        (sel) => !!sel,
      ),
    );
  }

  getInnerBoundarys() {
    return new BoundaryList(
      ...this.reduce((zones: Boundary[], zone) => zones.concat(zone.getInnerZoneBoundarys()), []).filter(
        (sel) => !!sel,
      ),
    );
  }

  getSampleZones() {
    return new SampleZoneList(...this.map((zone) => zone.getSampleZone()).filter((sel) => !!sel));
  }
}
